<template>
  <div data-test-id="CasePage" class="ems-page">
    <NavBar>
      <template #left>
        <span class="text-red" data-test-id="closeCaseButton" role="button" tabindex="0" @click="onCloseCase"
          >Close Case</span
        >
      </template>
      <template #center>
        <span>
          <b>{{ twiageCase.caseNumber }}</b>
        </span>
      </template>
      <template #right>
        <Messages />
      </template>
    </NavBar>
    <b-container>
      <PatientSummary v-if="hasPatientInfo" :name="patientName" :date-of-birth="patientDateOfBirth" />

      <div class="container-grid mb-2">
        <AgeAndGender
          :age="twiageCase.age || null"
          :age-period="twiageCase.agePeriod || 3"
          :gender="twiageCase.gender"
          class="flex-grow-1"
          @modify-case="onModifyCase"
        />

        <PatientInfo
          v-if="patientInfoForm"
          :form="patientInfoForm"
          :data="twiageCase.patientInfo"
          @modify-case="onModifyCase"
        />
      </div>

      <div class="mb-2">
        <Protocol
          v-if="aiEnhancedProtocolsEnabled"
          :twiage-case="twiageCase"
          @new-question="handleNewQuestion"
          @new-answer="handleNewAnswer"
          @new-feedback="handleNewFeedback"
        >
          <button type="button" class="ai-protocol-btn" data-test-id="ProtocolAIButton">
            <span class="label">AI Protocol Assistant</span>
          </button>
        </Protocol>
      </div>

      <b-row>
        <b-col>
          <CaseTabs>
            <template #complaints>
              <div class="tab-wrapper clean-scrollbar" :class="tabWrapperClasses">
                <ComplaintsList
                  data-test-id="complaintsContent"
                  :twiage-case="twiageCase"
                  :complaints="complaints"
                  @modify-case="onModifyCase"
                />
              </div>
            </template>

            <template #media>
              <div class="tab-wrapper clean-scrollbar" :class="tabWrapperClasses">
                <MediaButton
                  label="Photo"
                  icon="camera"
                  :count="mediaCount('photo')"
                  :event-type="CaseUpdateEventType.photo"
                  @modify-case="onModifyCase"
                />
                <MediaButton
                  label="ID"
                  icon="person-fill"
                  :count="mediaCount('id')"
                  :event-type="CaseUpdateEventType.id"
                  @modify-case="onModifyCase"
                />
                <MediaButton
                  label="EKG"
                  icon="heart-fill"
                  :count="mediaCount('ekg')"
                  :description="ekgButtonDescription"
                  :monitors="monitors"
                  :form-data="linkedMonitor"
                  :modal-content-style="{ 'min-height': '60px' }"
                  :event-type="CaseUpdateEventType.ekg"
                  @modify-case="onModifyCase"
                />
                <MediaButton
                  label="Video"
                  icon="camera-video-fill"
                  :count="mediaCount('video')"
                  :event-type="CaseUpdateEventType.video"
                  @modify-case="onModifyCase"
                />
                <MediaButton
                  label="Voice Memo"
                  icon="mic-fill"
                  :count="mediaCount('voicememo')"
                  :modal-content-style="{ 'min-height': '150px' }"
                  :event-type="CaseUpdateEventType.voiceMemo"
                  @modify-case="onModifyCase"
                />
                <LiveConsultMediaButton label="Live Consult" icon="camera-video-fill" @modify-case="onModifyCase" />
                <CaseTransferMediaButton label="Case Transfer" icon="arrow-left-right" />
              </div>
            </template>

            <template #chat>
              <Chat :messages="messages" :tab-wrapper-classes="tabWrapperClasses" @modify-case="onModifyCase" />
            </template>
          </CaseTabs>
        </b-col>
      </b-row>
      <hospital
        ref="hospitalSelector"
        :hospitals="selectedTeam.hospitals"
        :destination-hospital="twiageCase.destinationHospital"
        @modify-case="onModifyCase"
      />
    </b-container>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";

import NavBar from "@/App/components/NavBar.vue";
import Messages from "@/App/components/Messages.vue";
import CaseTabs from "@/Case/components/CaseTabs.vue";
import MediaButton from "@/Case/components/MediaButton.vue";
import LiveConsultMediaButton from "@/Case/components/LiveConsultMediaButton.vue";
import ComplaintsList from "@/Case/components/ComplaintsList.vue";
import AgeAndGender from "@/Case/components/AgeAndGender.vue";
import Hospital from "@/Case/components/Hospital.vue";
import Chat from "@/Case/components/Chat.vue";
import PatientInfo from "@/Case/components/PatientInfo";
import PatientSummary from "@/Case/components/PatientSummary";
import CaseTransferMediaButton from "@/Case/components/CaseTransferMediaButton.vue";
import { showConfirmDialog, showOkDialog } from "@/Utils/Dialogs";
import { CaseUpdateEventType } from "@/Case/CaseEvent";
import Protocol from "@/Protocol/Protocol.vue";
import { getClientTime } from "@/Utils/getClientTime";

export default {
  name: "Case",
  components: {
    Protocol,
    PatientInfo,
    PatientSummary,
    AgeAndGender,
    CaseTabs,
    MediaButton,
    LiveConsultMediaButton,
    NavBar,
    Messages,
    ComplaintsList,
    Hospital,
    Chat,
    CaseTransferMediaButton,
  },
  data() {
    return {
      autoCloseTimer: undefined,
    };
  },
  computed: {
    CaseUpdateEventType() {
      return CaseUpdateEventType;
    },
    ...mapGetters("TeamSelection", {
      selectedTeam: "selectedTeam",
      monitors: "monitors",
      patientInfoForm: "patientInfoForm",
    }),
    ...mapGetters("Case", {
      twiageCase: "twiageCase",
      destinationHospital: "destinationHospital",
      messages: "messages",
      mediaCount: "mediaCount",
      complaints: "complaints",
      linkedMonitor: "linkedMonitor",
      patientName: "patientName",
      patientDateOfBirth: "patientDateOfBirth",
      hasPatientInfo: "hasPatientInfo",
      missingRequiredPatientFields: "missingRequiredPatientFields",
    }),
    ...mapGetters("Login", {
      user: "user",
    }),
    ...mapGetters("PushNotification", ["registrationToken"]),
    ...mapGetters("Voip", {
      isVoipCallInProgress: "isCallInProgress",
    }),
    ekgButtonDescription() {
      if (this.linkedMonitor) {
        return `Device/Unit ${this.linkedMonitor.name}. Patient ${this.linkedMonitor.patientId}`;
      }
      if (this.monitors.length) {
        return "Monitor Integration Available";
      }
      return "";
    },
    tabWrapperClasses() {
      const { destinationHospital } = this.twiageCase;
      const isDestinationHospitalNonTwiage = destinationHospital && destinationHospital.nonTwiage;

      return {
        "non-twiage": isDestinationHospitalNonTwiage && !this.isVoipCallInProgress,
        "with-voip-call": !isDestinationHospitalNonTwiage && this.isVoipCallInProgress,
        "non-twiage-with-voip-call": isDestinationHospitalNonTwiage && this.isVoipCallInProgress,
        "with-patient-info": this.hasPatientInfo,
      };
    },
    aiEnhancedProtocolsEnabled() {
      return this.$store.getters["Login/user"]?.features?.find((feature) => feature.name === "aiEnhancedProtocols")
        ?.enabled;
    },
  },
  watch: {
    registrationToken: function () {
      this.subscribePushNotification(`ems-${this.twiageCase.id}`);
    },
  },
  async mounted() {
    this.loadTransferredComplaints();
    if (this.complaints.length === 0) {
      await this.loadComplaints(this.selectedTeam.complaintSet);
    }
    const channelId = this.twiageCase?.channelId;
    if (channelId) {
      this.onCaseCreated(channelId);
    } else {
      this.unwatchTwiageCase = this.$watch(
        "twiageCase",
        (twiageCase) => {
          if (twiageCase?.channelId) {
            this.onCaseCreated(twiageCase.channelId);
            this.unwatchTwiageCase();
          }
        },
        { deep: true },
      );
    }
  },
  destroyed() {
    if (this.autoCloseTimer) {
      clearTimeout(this.autoCloseTimer);
    }
  },
  methods: {
    ...mapActions("TeamSelection", ["loadComplaints"]),
    ...mapActions("Case", ["modifyCase", "closeCase", "trackLocation", "loadTransferredComplaints"]),
    ...mapActions("PubNubSubscription", ["subscribeChatChannel", "unsubscribeChatChannel"]),
    ...mapActions("PushNotification", {
      subscribePushNotification: "subscribe",
    }),
    onCloseCase() {
      if (this.okToCloseCase()) {
        if (this.isVoipCallInProgress) {
          showOkDialog(this.$bvModal, "Warning", "End current call to close case");
        } else {
          if (this.missingRequiredPatientFields.length > 0) {
            showOkDialog(
              this.$bvModal,
              "Warning",
              `Please fill in the following fields in Patient Info to complete this case: ${this.missingRequiredPatientFields.join(
                ", ",
              )}.`,
            );
          } else {
            showConfirmDialog(
              this.$bvModal,
              "Would you like to close the case?",
              "If you close the case you will lose the connection with the hospital but all information will remain on the servers.",
              "Close Case",
              this.handleCloseCase,
            );
          }
        }
      } else {
        showOkDialog(
          this.$bvModal,
          "Warning",
          "No hospital selected! Please select a hospital before closing the case.",
          () => {
            this.$refs.hospitalSelector.$refs.hospitalPicker.click();
          },
        );
      }
    },
    onModifyCase(event) {
      this.modifyCase(event);
    },
    okToCloseCase() {
      if (this.selectedTeam.hospitals?.length > 0) {
        return this.twiageCase?.destinationHospital?.id;
      } else {
        return true;
      }
    },

    async handleCloseCase() {
      if (this.twiageCase?.channelId) {
        this.unsubscribeChatChannel({ channelId: this.twiageCase.channelId });
      }

      await this.closeCase();
    },
    validateAutoClose() {
      if (this.twiageCase?.createdAt && this.selectedTeam?.maxCaseLength) {
        const createdAtDate = new Date(this.twiageCase.createdAt);
        const autoCloseDate = new Date(createdAtDate.getTime() + this.selectedTeam.maxCaseLength * 60000);
        const remainingTime = autoCloseDate.getTime() - new Date().getTime();

        if (remainingTime > 0) {
          this.autoCloseTimer = setTimeout(() => {
            this.handleCloseCase();
          }, remainingTime);
        } else {
          this.handleCloseCase();
        }
      }
    },
    onCaseCreated(channelId) {
      if (channelId) {
        this.subscribeChatChannel({ channelId, uuid: this.user.id });
        this.trackLocation();
        if (this.twiageCase?.type === "emsED" || this.twiageCase?.type === "hospitalED") {
          this.validateAutoClose();
        }
      }
    },

    handleNewQuestion({ question }) {
      this.modifyCase({
        eventType: CaseUpdateEventType.chat,
        data: {
          content: `Q: ${question}`,
          clientCreatedAt: getClientTime(),
          timestamp: Date.now(),
          source: "web_ems",
          senderName: this.user.displayName,
          senderOrganization: this.selectedTeam.name,
          type: "CHAT",
        },
      });
    },

    handleNewAnswer({ answer }) {
      this.modifyCase({
        eventType: CaseUpdateEventType.chat,
        data: {
          content: `A: ${answer}`,
          clientCreatedAt: getClientTime(),
          timestamp: Date.now(),
          source: "web_ems",
          senderName: this.user.displayName,
          senderOrganization: this.selectedTeam.name,
          type: "CHAT",
        },
      });
    },

    handleNewFeedback({ feedback }) {
      this.modifyCase({
        eventType: CaseUpdateEventType.chat,
        data: {
          content: `Feedback: ${feedback}`,
          clientCreatedAt: getClientTime(),
          timestamp: Date.now(),
          source: "web_ems",
          senderName: this.user.displayName,
          senderOrganization: this.selectedTeam.name,
          type: "CHAT",
        },
      });
    },
  },
};
</script>

<style scoped>
.container-grid {
  display: flex;
  gap: 1rem;
}

.text-red {
  color: red;
}

.tab-wrapper {
  height: calc(100vh - 250px);
  max-height: 65vh;
  overflow-y: auto;
}

.tab-wrapper.non-twiage {
  height: calc(100vh - 326px);
  max-height: calc(65vh - 76px);
}

.tab-wrapper.with-voip-call {
  height: calc(100vh - 326px);
  max-height: calc(65vh - 76px);
}

.tab-wrapper.with-patient-info {
  height: calc(100vh - 270px);
  max-height: calc(65vh - 60px);
}

.tab-wrapper.non-twiage-with-voip-call {
  height: calc(100vh - 402px);
  max-height: calc(65vh - 152px);
}

.ai-protocol-btn {
  width: 100%;
  padding: 0.35rem;
  border-radius: 0.5rem;
  font-weight: bold;
  font-size: 1rem;
  border: 2px solid #bf1311;
  background-color: #000000;
  color: #bf1311;
}
</style>
